import { apollo } from './graphql';
import { SCHEDULE_EXPORT, POLL_EXPORT, SCHEDULE_FILE_EXPORT } from './queries/export';
import { wait } from '../util/util';
import { SortDirection } from './history';
import { MerchStatsProps } from './merchStatistics';

interface Export {
  id: string;
  url?: string;
}

async function pollExport(exportObject: Export): Promise<string> {
  if (exportObject.url) {
    return exportObject.url;
  }
  const result = await apollo.query({
    query: POLL_EXPORT,
    variables: { id: exportObject.id },
    fetchPolicy: 'network-only',
  });
  const updatedExport = result.data?.export;
  if (updatedExport?.url) {
    return updatedExport.url;
  }
  if (updatedExport?.error) {
    throw new Error(updatedExport.error);
  }
  await wait(1);
  return await pollExport(exportObject);
}

interface BookKeepingExport {
  type: 'book_keeping';
  arguments: {
    startDate: Date;
    endDate: Date;
  };
}

interface ClearingInvoiceDebugExport {
  type: 'clearing_debug';
  arguments: {
    invoiceId: string;
  };
}

interface ProductListExport {
  type: 'product_list';
  arguments: {
    status?: string[];
    searchTerm?: string;
    sortBy?: string;
    sortDir?: SortDirection;
  };
}

interface StatVariantsExport {
  type: 'ProductVariantStatistics';
  arguments: Partial<MerchStatsProps> & { productType: string };
}

interface VenueLayoutExport {
  type: 'VenueLayout';
  arguments: { venueLayoutId: string };
}

interface SalesPostcodeExport {
  type: 'SalesPostcode';
  arguments: { sellableId: string; sellableType: 'product' | 'category'; from?: Date; to?: Date };
}

interface TicketsByPricingCategoryExport {
  type: 'TicketsByPricingCategory';
  arguments: { eventId: string };
}

interface PhysicalSalesChartExport {
  type: 'physical_sales_chart';
  file: File;
}

interface ChartPreviewExport {
  type: 'charts';
  arguments: {
    country: 'de' | 'at' | 'ch';
    preview: true;
    day: Date;
  };
}

interface BookChartsPreviewExport {
  type: 'book_charts';
  arguments: {
    preview: true;
    day: Date;
  };
}

interface ChartOverridesExport {
  type: 'ChartOverrides';
  arguments: { productId: string };
}

interface EventTurnoverExport {
  type: 'EventTurnover';
  arguments: { terms: string[]; from: Date | null; to: Date | null };
}
interface TicketPricingExport {
  type: 'TicketPricing';
  arguments: { eventId: string };
}

interface TicketPreviewFullPdfExport {
  type: 'TicketPreviewFullPdf';
  arguments: { eventId: string };
}

interface BundleConfigExport {
  type: 'BundleConfig';
  arguments: { productId: string };
}

interface PassbookPreviewExport {
  type: 'PassbookPreview';
  arguments: { productId: string };
}

interface VoucherCodesExport {
  type: 'VoucherCodes';
  arguments: { voucherCampaignId: string };
}

interface TicketCodesExport {
  type: 'TicketCodes';
  arguments: { eventId: string; full?: boolean };
}

interface EventAllocationsExport {
  type: 'EventAllocations';
  arguments: { eventId: string };
}

interface EventSeatStatusesExport {
  type: 'EventSeatStatuses';
  arguments: { eventId: string };
}

type ExportDefinition =
  | BookKeepingExport
  | ClearingInvoiceDebugExport
  | ProductListExport
  | StatVariantsExport
  | VenueLayoutExport
  | SalesPostcodeExport
  | TicketsByPricingCategoryExport
  | PhysicalSalesChartExport
  | ChartPreviewExport
  | ChartOverridesExport
  | EventTurnoverExport
  | TicketPricingExport
  | TicketPreviewFullPdfExport
  | BundleConfigExport
  | PassbookPreviewExport
  | BookChartsPreviewExport
  | VoucherCodesExport
  | TicketCodesExport
  | EventAllocationsExport
  | EventSeatStatusesExport;

export async function runExport(definition: ExportDefinition, exportFile?: boolean): Promise<void> {
  const result = await apollo.mutate({
    mutation: exportFile ? SCHEDULE_FILE_EXPORT : SCHEDULE_EXPORT,
    variables: { arguments: {}, ...definition },
  });
  const exportObject: Export = result.data && result.data[Object.keys(result.data)[0]].export;
  await wait(1);
  const url = await pollExport(exportObject);
  if (!exportFile) {
    window.open(url, '_blank');
  }
}

export async function runClearingInvoiceDebugExport(invoiceId: string): Promise<void> {
  await runExport({ type: 'clearing_debug', arguments: { invoiceId } });
}

export async function runProductListExport(args: ProductListExport['arguments']): Promise<void> {
  await runExport({ type: 'product_list', arguments: args });
}

export async function runStatVariantsExport(args: StatVariantsExport['arguments']): Promise<void> {
  await runExport({ type: 'ProductVariantStatistics', arguments: args });
}

export async function runVenueLayoutExport(args: VenueLayoutExport['arguments']): Promise<void> {
  await runExport({ type: 'VenueLayout', arguments: args });
}

export async function runSalesPostcodeExport(args: SalesPostcodeExport['arguments']): Promise<void> {
  await runExport({ type: 'SalesPostcode', arguments: args });
}

export async function runTicketsByPricingCategoryExport(
  args: TicketsByPricingCategoryExport['arguments']
): Promise<void> {
  await runExport({ type: 'TicketsByPricingCategory', arguments: args });
}

export async function runPhysicalSalesChartExport(file: PhysicalSalesChartExport['file']): Promise<void> {
  await runExport({ type: 'physical_sales_chart', file }, true);
}

export async function runChartExportPreview(args: Omit<ChartPreviewExport['arguments'], 'preview'>): Promise<void> {
  await runExport({ type: 'charts', arguments: { ...args, preview: true } });
}

export async function runBookChartsExportPreview(
  args: Omit<BookChartsPreviewExport['arguments'], 'de' | 'preview'>
): Promise<void> {
  await runExport({ type: 'book_charts', arguments: { ...args, preview: true } });
}
export async function runChartOverridesExport(args: ChartOverridesExport['arguments']): Promise<void> {
  await runExport({ type: 'ChartOverrides', arguments: args });
}

export async function runEventTurnoverExport(args: EventTurnoverExport['arguments']): Promise<void> {
  await runExport({ type: 'EventTurnover', arguments: args });
}

export async function runTicketPricingExport(args: TicketPricingExport['arguments']): Promise<void> {
  await runExport({ type: 'TicketPricing', arguments: args });
}

export async function runTicketPreviewFullPdfExport(args: TicketPreviewFullPdfExport['arguments']): Promise<void> {
  await runExport({ type: 'TicketPreviewFullPdf', arguments: args });
}

export async function runBundleConfigExport(args: BundleConfigExport['arguments']): Promise<void> {
  await runExport({ type: 'BundleConfig', arguments: args });
}

export async function runPassbookPreviewExport(args: PassbookPreviewExport['arguments']): Promise<void> {
  await runExport({ type: 'PassbookPreview', arguments: args });
}

export async function runVoucherCodesExport(args: VoucherCodesExport['arguments']): Promise<void> {
  await runExport({ type: 'VoucherCodes', arguments: args });
}

export async function runTicketCodesExport(args: TicketCodesExport['arguments']): Promise<void> {
  await runExport({ type: 'TicketCodes', arguments: args });
}

export async function runEventAllocationsExport(args: EventAllocationsExport['arguments']): Promise<void> {
  await runExport({ type: 'EventAllocations', arguments: args });
}

export async function runEventSeatStatusesExport(args: EventSeatStatusesExport['arguments']): Promise<void> {
  await runExport({ type: 'EventSeatStatuses', arguments: args });
}
